/* ============================================================================
 * Raisy /pitchdeck — THE 30 MARKET VALUES, as a PRINT-ONLY table.
 *
 * WHY PRINT ONLY: the deck's presented slides are the story; a 6x5 financial grid
 * on a hero slide makes it technical (PO: "dont make the deck technical"). But the
 * numbers must still be *in* the deck rather than only in the model, and the PDF is
 * the leave-behind that gets read line by line. So the screen keeps the drawn
 * circles and this table rides along in the printed page — the same convention the
 * market slide already uses for its sources (`.pd-bb-sources`).
 *
 * The `./marketByYear.ts` module this reads is GENERATED from the Raisy financial
 * model and carries its OWN provenance header — the source file, the assumption
 * inputs, the generated date and the basis of each series. Read that header; do not
 * restate the path here. (This comment used to name the generator and its inputs, and
 * both moved within the hour — FM-0011 → FM-0013 KERNEL.) Regenerate, never
 * hand-edit. 6 series x 5 years = 30 values, absolute and growth side by side.
 *
 * To make it visible on screen too, drop `pd-print-only` from the wrapper below.
 * ========================================================================== */
import {
  MARKET_CAGR,
  MARKET_SAM,
  MARKET_SAM_GROWTH,
  MARKET_SOM,
  MARKET_SOM_GROWTH,
  MARKET_TAM,
  MARKET_TAM_GROWTH,
  MARKET_YEARS,
} from './marketByYear';

/** USD → a short, fixed-width figure. Never `toFixed` a raw dollar amount. */
const usd = (v: number) =>
  v >= 1e9
    ? `$${(v / 1e9).toFixed(2)}B`
    : v >= 1e6
      ? `$${(v / 1e6).toFixed(2)}M`
      : `$${(v / 1e3).toFixed(0)}K`;

/** Growth. `null` is a real state — the first year has no prior year to grow from,
 *  and an em-dash says that honestly where a 0% would claim a measurement. */
const pct = (g: number | null) => (g === null ? '—' : `+${(g * 100).toFixed(1)}%`);

const ROWS: { label: string; series: (number | null)[]; fmt: (v: number) => string }[] = [
  { label: 'TAM', series: MARKET_TAM, fmt: usd },
  { label: 'TAM growth', series: MARKET_TAM_GROWTH, fmt: pct },
  { label: 'SAM', series: MARKET_SAM, fmt: usd },
  { label: 'SAM growth', series: MARKET_SAM_GROWTH, fmt: pct },
  { label: 'SOM', series: MARKET_SOM, fmt: usd },
  { label: 'SOM growth', series: MARKET_SOM_GROWTH, fmt: pct },
];

const CELLS = ROWS.length * MARKET_YEARS.length; // 30

export function MarketByYearTable() {
  return (
    <div className="pd-print-only pd-bb-sources pd-mtable">
      <p>
        <b>Market model — all {MARKET_YEARS.length} years, all {CELLS} values.</b> Absolute
        figures and year-on-year growth, 2026 → {MARKET_YEARS[MARKET_YEARS.length - 1]}. Source:
        the Raisy financial model — the exact source file, inputs and basis are in the
        provenance header of the generated ./marketByYear.ts this deck reads.
      </p>
      {/* The CAGR used to live only in the TAM tooltip, so the deck could state
          "+46%/yr" beside one circle and nothing beside the other two. Stated here
          for all three, derived from the series — never typed. The last sentence is
          deliberate: SOM is REVENUE, not a share of SAM, so the concentric drawing
          must not be read as containment. */}
      <p>
        <b>
          Span CAGR {MARKET_YEARS[0]} → {MARKET_YEARS[MARKET_YEARS.length - 1]} (endpoint to
          endpoint):
        </b>{' '}
        TAM {(MARKET_CAGR.tam * 100).toFixed(1)}% · SAM {(MARKET_CAGR.sam * 100).toFixed(1)}% · SOM{' '}
        {(MARKET_CAGR.som * 100).toFixed(1)}%. SAM compounds because one cohort of raising
        founders is added each year and stays addressable (an account raises more than once).
        SOM is bottom-up revenue from our own customer build — it is not a share of SAM, and
        SAM is not a subset of TAM: they are three measures of different things.
      </p>
      <table>
        <thead>
          <tr>
            <th scope="col">Series</th>
            {MARKET_YEARS.map((y) => (
              <th scope="col" key={y}>
                {y}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr key={r.label}>
              <th scope="row">{r.label}</th>
              {r.series.map((v, i) => (
                <td key={MARKET_YEARS[i]}>{v === null ? '—' : r.fmt(v)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
